import { Box, Text, Tooltip, Skeleton, Modal, ScrollArea } from '@mantine/core';
import { useState } from 'react';
import { getEvidenceDisplayTitle, stripHtml } from '../../../utils';
import { getOriginalItemViewLabel } from '../../../utils/evidence/businessCategoryOriginalLabels';

/**
 * Discovery-only variant: optional title suppression when the title is shown in the accordion trigger.
 */
export function DiscoveryCardTextArea({
  item,
  isLoadingMl = false,
  suppressTitle = false,
}: {
  item: any;
  isLoadingMl?: boolean;
  suppressTitle?: boolean;
}) {
  const title = item?.title;
  const text = item?.text;
  const excerpt = item?.excerpt;
  // A blank title falls back to the first few words of the description — the description
  // box below still shows the full, untruncated text unchanged.
  const strippedText = text ? stripHtml(text) : '';
  const displayTitle = getEvidenceDisplayTitle(title, text);
  const fullDisplayTitle = title || strippedText;
  const excerptsArr: { excerpt?: string; fullQuote?: string }[] = item?.excerpts || [];
  const businessCategory = item?.cxItem?.businessCategory as string | undefined;
  const [originalOpened, setOriginalOpened] = useState(false);
  const showOriginalLink =
    businessCategory === 'generalFeedback' || businessCategory === 'other';
  const originalLinkLabel = getOriginalItemViewLabel(businessCategory);

  return (
    <Box mt={suppressTitle ? 0 : 15} sx={{}}>
      {displayTitle && !suppressTitle && (
        <Tooltip label={fullDisplayTitle} multiline maw={400}>
          <Text fz={14} fw={600} lineClamp={1} mb={14} lh="18px">
            {displayTitle}
          </Text>
        </Tooltip>
      )}

      {text && (
        <Box py={8} px={12} bg="#F8F9FA" sx={{ borderLeft: '2px solid rgba(92, 92, 235, 0.6)' }}>
          <Text
            fz={14}
            color="#212529"
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: '18px',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {isLoadingMl ? (
              <div>
                <Skeleton height={14} width="100%" mb={8} />
                <Skeleton height={14} width="95%" mb={8} />
                <Skeleton height={14} width="85%" />
              </div>
            ) : item?.ml?.functionalitySummary ? (
              <div>
                <b>Gap/Request: </b>
                {stripHtml(item?.ml?.functionalitySummary)}
                <br />
                <br />
                <b>Underlying need: </b>
                {stripHtml(item?.ml?.functionalityGap)}
              </div>
            ) : (
              stripHtml(text)
            )}
          </Text>
        </Box>
      )}
      {excerpt && excerpt !== '' && (
        <Box py={8} px={12} bg="#F8F9FA" sx={{ borderLeft: '2px solid rgba(92, 92, 235, 0.6)' }}>
          <Box fz={14} lh="18px">
            <Text mb="5px" fw={600} style={{ color: 'black', fontSize: '14px' }}>
              Excerpt:{' '}
            </Text>
            <Text
              color="#585C68"
              sx={{
                whiteSpace: 'pre-line',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {stripHtml(excerpt)}
            </Text>
          </Box>
        </Box>
      )}
      {!excerpt && excerptsArr.length > 0 && (
        <Box py={8} px={12} bg="#F8F9FA" sx={{ borderLeft: '2px solid rgba(92, 92, 235, 0.6)' }}>
          <Text mb="5px" fw={600} style={{ color: 'black', fontSize: '14px' }}>
            {excerptsArr.length === 1 ? 'Excerpt:' : 'Excerpts:'}
          </Text>
          {excerptsArr.map((excerptItem, index) => (
            <Box key={index} mb={index < excerptsArr.length - 1 ? 8 : 0}>
              <Text
                color="#585C68"
                sx={{
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {stripHtml(excerptItem?.excerpt || 'N/A')}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {showOriginalLink && text ? (
        <>
          <Modal
            opened={originalOpened}
            onClose={() => setOriginalOpened(false)}
            title={originalLinkLabel}
            size="lg"
            centered
          >
            <ScrollArea h={420}>
              <Text fz={14} sx={{ whiteSpace: 'pre-line' }}>
                {stripHtml(text)}
              </Text>
            </ScrollArea>
          </Modal>
          <Box
            w="100%"
            py={8}
            px={12}
            bg="#F8F9FA"
            sx={{ borderLeft: '2px solid rgba(92, 92, 235, 0.6)' }}
          >
            <Text
              mt={4}
              fz={13}
              color="#5C5CEB"
              sx={{ cursor: 'pointer' }}
              onClick={() => setOriginalOpened(true)}
            >
              {originalLinkLabel}
            </Text>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
